import { useState, useEffect, useCallback } from "react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { logActivity } from "../lib/activity-logger";
import type {
  ClaimsProject,
  ClaimsCategory,
  ClaimsSubcategory,
} from "../types/database.types";
import { calculateContractValue } from "../lib/calculations";
import { fetchCoverImagesForProjects } from "../lib/projectImages";
import {
  type ClaimDueStatus,
  getClaimDueInfo,
  getNextClaimDate,
  getStatusSortPriority,
} from "../lib/claimDueDate";
import { logError } from "../lib/logError";
import {
  canDeleteProject,
  deleteProjectRow,
  ProjectDeleteRefusedError,
} from "../lib/projectDelete";
import { useAuth } from "./useAuth";

export interface ProjectWithDetails extends ClaimsProject {
  categories?: (ClaimsCategory & { subcategories?: ClaimsSubcategory[] })[];
  contractValue?: number;
  approvedVariationsTotal?: number;
  progressTotal?: number; // Total claimed from progress
  variationsCount?: number; // Count of variations
  claimDueStatus?: ClaimDueStatus;
  claimDueLabel?: string;
  currentMonthClaimed?: boolean;
  nextClaimDate?: Date;
  // P&L Metrics for the Dashboard
  defectRatio?: number;
  pnlConstructionIncome?: number;
  pnlPatching?: number;
  budgetStatus?: 'Kicking goals' | 'Good Budget' | 'On Budget' | 'Under Budget' | 'Over Budget' | null;
  actualMargin?: number;
  estimateProfit?: number;
  totalCosts?: number;
  budgetTooltip?: string;
  // Reconciliation status (data-driven, not calendar-driven)
  reconTotalReports?: number;
  reconReconciledCount?: number;
  reconUnreconciledCount?: number;
  lastReconciledAt?: string;
  lastReconciledByName?: string;
  lastReconciledMonth?: number;
  lastReconciledYear?: number;
  // Project images
  coverImageUrl?: string;
  imageCount?: number;
}

export function useProjects() {
  const { orgMembership } = useAuth();
  const [projects, setProjects] = useState<ProjectWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // ── SINGLE QUERY via claims_project_dashboard view ──
      // Replaces ~7 queries per project (N+1) with one aggregated query.
      /* eslint-disable @typescript-eslint/no-explicit-any -- view not in generated types yet */
      const { data: dashboardRows, error: dashboardError } = await (supabase as any)
        .from("claims_project_dashboard")
        .select("*")
        .order("job_location", { ascending: true });
      /* eslint-enable @typescript-eslint/no-explicit-any */

      if (dashboardError) throw dashboardError;

      // ── Fetch reconciliation aggregates per project (one query) ──
      const projectIds = (dashboardRows || []).map((r: Record<string, unknown>) => r.id as string);
      const reconMap = new Map<string, {
        total_reports: number;
        reconciled_count: number;
        unreconciled_count: number;
        latest_reconciled_at: string | null;
        latest_reconciled_by: string | null;
        latest_reconciled_month: number | null;
        latest_reconciled_year: number | null;
      }>();

      if (projectIds.length > 0) {
        // Query 1: Count total reports per project
        const { data: reportCounts } = await supabase
          .from('claims_reports')
          .select('project_id')
          .in('project_id', projectIds);

        // Query 2: Latest reconciled per project (ordered DESC so first per project = latest)
        const { data: reconRows } = await supabase
          .from('claims_reports')
          .select('project_id, reconciled_at, reconciled_by_name, claim_month, claim_year')
          .not('reconciled_at', 'is', null)
          .in('project_id', projectIds)
          .order('reconciled_at', { ascending: false });

        // Count total reports per project
        const totalCountMap = new Map<string, number>();
        (reportCounts || []).forEach((r: Record<string, unknown>) => {
          const pid = r.project_id as string;
          totalCountMap.set(pid, (totalCountMap.get(pid) || 0) + 1);
        });

        // Count reconciled reports per project and get latest
        const reconCountMap = new Map<string, number>();
        const latestReconMap = new Map<string, {
          at: string; by: string | null; month: number; year: number;
        }>();
        (reconRows || []).forEach((row: Record<string, unknown>) => {
          const pid = row.project_id as string;
          reconCountMap.set(pid, (reconCountMap.get(pid) || 0) + 1);
          if (!latestReconMap.has(pid)) {
            latestReconMap.set(pid, {
              at: row.reconciled_at as string,
              by: row.reconciled_by_name as string | null,
              month: row.claim_month as number,
              year: row.claim_year as number,
            });
          }
        });

        // Build combined reconMap
        projectIds.forEach((pid: string) => {
          const total = totalCountMap.get(pid) || 0;
          const reconciled = reconCountMap.get(pid) || 0;
          const latest = latestReconMap.get(pid);
          reconMap.set(pid, {
            total_reports: total,
            reconciled_count: reconciled,
            unreconciled_count: total - reconciled,
            latest_reconciled_at: latest?.at || null,
            latest_reconciled_by: latest?.by || null,
            latest_reconciled_month: latest?.month || null,
            latest_reconciled_year: latest?.year || null,
          });
        });
      }

      // ── Fetch cover images for all projects (one query) ──
      const coverImageMap = await fetchCoverImagesForProjects(projectIds);

      const projectsWithDetails: ProjectWithDetails[] = (dashboardRows || []).map(
        (row: Record<string, unknown>) => {
          const contractValue = Number(row.contract_value) || 0;
          const approvedVariations = Number(row.approved_variations_total) || 0;
          const variationsCount = Number(row.variations_count) || 0;
          const progressTotal = Number(row.progress_total) || 0;
          const totalCosts = Number(row.total_costs) || 0;

          const estimateProfit = Number(row.estimate_profit) || 0;
          const pnlConstructionIncome = Number(row.pnl_construction_income) || 0;
          const pnlPatching = Number(row.pnl_patching) || 0;

          // Parse report months from JSON for claim due calculation
          const reportMonths = (row.report_months as Array<{ claim_month: number; claim_year: number }>) || [];
          const claimDay = (row.claim_day as number) ?? 26;
          const claimInfo = getClaimDueInfo(claimDay, reportMonths);
          const nextClaimDate = getNextClaimDate(claimDay);

          // P&L budget status calculation (same logic, server-computed inputs)
          const totalIncome = progressTotal + approvedVariations;
          const actualMargin = totalIncome - totalCosts;
          // Defect ratio: patching ÷ P&L income (Xero-synced actuals)
          const defectRatio = pnlConstructionIncome > 0 ? (pnlPatching / pnlConstructionIncome) * 100 : 0;

          let budgetStatus: ProjectWithDetails['budgetStatus'] = null;
          if (estimateProfit > 0) {
            const diffPct = (actualMargin - estimateProfit) / estimateProfit;
            const absoluteDiff = Math.abs(diffPct);

            if (diffPct > 0.2) budgetStatus = 'Kicking goals';
            else if (absoluteDiff <= 0.05) budgetStatus = 'Good Budget';
            else if (diffPct > 0) budgetStatus = 'Under Budget';
            else if (diffPct > -0.1) budgetStatus = 'On Budget';
            else budgetStatus = 'Over Budget';
          }

          // Spread original project fields (the view includes all claims_projects columns)
          const {
            contract_value: _cv,
            approved_variations_total: _avt,
            variations_count: _vc,
            progress_total: _pt,
            total_costs: _tc,
            patching_expenses: _pe,
            estimate_profit: _ep,
            report_months: _rm,
            pnl_construction_income: _pci,
            pnl_patching: _pp,
            ...projectFields
          } = row;

          // Reconciliation status: data-driven (count unreconciled reports)
          const recon = reconMap.get(row.id as string);

          // Build insightful budget tooltip
          const fmtK = (v: number) => {
            const abs = Math.abs(v);
            const sign = v < 0 ? '-' : '';
            if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
            if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
            return `${sign}$${abs.toFixed(0)}`;
          };
          let budgetTooltip = '';
          if (budgetStatus && estimateProfit > 0) {
            const diffPct = ((actualMargin - estimateProfit) / estimateProfit * 100).toFixed(1);
            budgetTooltip = [
              `Income: ${fmtK(totalIncome)} (Claims ${fmtK(progressTotal)} + Vars ${fmtK(approvedVariations)})`,
              `Costs: ${fmtK(totalCosts)}${totalCosts === 0 ? ' (not synced from Xero)' : ''}`,
              `Actual Margin: ${fmtK(actualMargin)}`,
              `Estimated Profit: ${fmtK(estimateProfit)}`,
              `Diff: ${diffPct}% ${Number(diffPct) >= 0 ? 'above' : 'below'} estimate`,
            ].join('\n');
          }

          return {
            ...projectFields,
            contractValue,
            approvedVariationsTotal: approvedVariations,
            progressTotal,
            variationsCount,
            claimDueStatus: claimInfo.status,
            claimDueLabel: claimInfo.label,
            currentMonthClaimed: claimInfo.currentMonthClaimed,
            nextClaimDate,
            defectRatio,
            pnlConstructionIncome,
            pnlPatching,
            budgetStatus,
            actualMargin,
            estimateProfit,
            totalCosts,
            budgetTooltip,
            reconTotalReports: recon?.total_reports ?? 0,
            reconReconciledCount: recon?.reconciled_count ?? 0,
            reconUnreconciledCount: recon?.unreconciled_count ?? 0,
            lastReconciledAt: recon?.latest_reconciled_at ?? undefined,
            lastReconciledByName: recon?.latest_reconciled_by ?? undefined,
            lastReconciledMonth: recon?.latest_reconciled_month ?? undefined,
            lastReconciledYear: recon?.latest_reconciled_year ?? undefined,
            coverImageUrl: coverImageMap.get(row.id as string)?.url ?? undefined,
            imageCount: coverImageMap.get(row.id as string)?.totalCount ?? 0,
          } as ProjectWithDetails;
        },
      );

      // Sort: 1) Active projects first (by claim urgency), 2) Upcoming, 3) On Hold, 4) Completed
      const statusOrder: Record<string, number> = { active: 0, upcoming: 1, on_hold: 2, completed: 3 };
      projectsWithDetails.sort((a, b) => {
        const statusA = statusOrder[a.status || "active"] ?? 0;
        const statusB = statusOrder[b.status || "active"] ?? 0;
        if (statusA !== statusB) return statusA - statusB;
        // Within same status, sort by claim urgency (red → orange → green)
        const priorityA = getStatusSortPriority(a.claimDueStatus || "green");
        const priorityB = getStatusSortPriority(b.claimDueStatus || "green");
        if (priorityA !== priorityB) return priorityA - priorityB;
        const dateA = a.nextClaimDate?.getTime() || 0;
        const dateB = b.nextClaimDate?.getTime() || 0;
        return dateA - dateB;
      });

      setProjects(projectsWithDetails);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const createProject = async (projectData: {
    job_location: string;
    job_address: string;
    project_details?: string;
    normal_rate?: number;
    overtime_rate?: number;
  }) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { data, error } = await supabase
      .from("claims_projects")
      .insert({
        ...projectData,
        organization_id: requireOrganizationId(),
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;

    // Log project creation for activity tracking
    await logActivity({
      projectId: data.id,
      actionType: "CREATE",
      entityType: "PROJECT",
      entityId: data.id,
      description: `Created new project "${data.job_location}"`,
      metadata: { job_address: data.job_address },
    });

    await fetchProjects();
    return data;
  };

  // SEQ-212: delete the project row FIRST and only that row. Every child table
  // cascades from it in one statement, so a refused delete (not an org admin)
  // removes nothing. The old code deleted children first and the project last,
  // which let a member wipe a project's contents and then fail on the project.
  const deleteProject = async (projectId: string) => {
    if (!canDeleteProject(orgMembership)) throw new ProjectDeleteRefusedError();

    // Read the name for the log before the row is gone.
    const { data: projectToDelete } = await supabase
      .from("claims_projects")
      .select("job_location")
      .eq("id", projectId)
      .maybeSingle();

    await deleteProjectRow(supabase, projectId, canDeleteProject(orgMembership));

    // Logged after the delete succeeded, so a refused delete leaves no
    // "Deleted project" entry. project_id is omitted because the project no
    // longer exists; the id is kept in entity_id and metadata.
    await logActivity({
      actionType: "DELETE",
      entityType: "PROJECT",
      entityId: projectId,
      description: `Deleted project "${projectToDelete?.job_location || "Unknown"}"`,
      metadata: { project_id: projectId },
    });

    await fetchProjects();
  };

  return {
    projects,
    loading,
    error,
    fetchProjects,
    refreshProjects: fetchProjects,
    createProject,
    deleteProject,
  };
}

export function useProject(projectId: string | undefined) {
  const [project, setProject] = useState<ProjectWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = useCallback(async () => {
    if (!projectId) {
      setProject(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: projectData, error: projectError } = await supabase
        .from("claims_projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (projectError) throw projectError;

      const { data: categories } = await supabase
        .from("claims_categories")
        .select("*, subcategories:claims_subcategories(*)")
        .eq("project_id", projectId)
        .order("sort_order");

      const contractValue = calculateContractValue(categories || []);

      setProject({
        ...projectData,
        categories: categories || [],
        contractValue,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch project");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  return {
    project,
    loading,
    error,
    refreshProject: fetchProject,
  };
}

export function useExistingProjectNames() {
  const [names, setNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchNames() {
      try {
        const { data } = await supabase
          .from("claims_projects")
          .select("job_location");
        if (data) {
          setNames(data.map((p) => p.job_location).filter(Boolean));
        }
      } catch (error) {
        logError("Failed to fetch existing project names", error);
      } finally {
        setLoading(false);
      }
    }
    fetchNames();
  }, []);

  return { names, loading };
}
